/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.23.0
 * @since 1.22.0
 */

import type { FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { icons } from '@assets/index';
import { BadgeStatus, EllipseText, InfoBlock, LinkBlock } from '@components/common';
import { formatNumberToPhone, PhoneFormatType } from '@components/common/PhoneMask/formatNumberToPhone';
import { EColorStatus } from '@modules/app/appConstants';
import { EdmConnectionCancelModal } from '@modules/edmDelivery/components/EdmConnectionCancelModal';
import { EdmOperatorConnectionModal } from '@modules/edmDelivery/components/EdmOperatorConnectionModal';
import { EdmResponsibleEditModal } from '@modules/edmDelivery/components/EdmResponsibleEditModal';
import { EEdmOperatorConnectionMode } from '@modules/edmDelivery/constants';
import {
    EmdConnectionTitleStyled,
    EmdConnectionStatusContainerStyled,
    EmdConnectionInfoStyled,
    EmdConnectionLabelStyled,
    EmdResponsibleContactStyled,
    EmdConnectionOptionsStyled,
    EmdConnectionStatusStyled,
} from '@modules/edmDelivery/styled/EmdConnectionStatusStyled';
import { EdmConnectionId } from '@modules/edmDelivery/types';
import { setEdmChangeOperatorInitialValues } from '@modules/edmDelivery/util/setInitialFormValues';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectEdmOperatorConnectionTranslation } from '@modules/internationalization/intSelectors';
import { modalOpen } from '@modules/modal/modalActions';
import {
    EdmInfoLinkStyled,
    EdmInfoTitleStyled,
} from '@pages/PageAccountInfo/components/styled/AccountInfoDeliveryStyled';
import type { TClientId } from '@pages/PageAccountInfo/types';
import { EConnectionStatusId } from '@pages/PageAccountInfo/types/pageAccountInfoGeneralTypes';
import type { IEdmDeliveryInfo } from '@pages/PageAccountInfo/types/pageAccountInfoGeneralTypes';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { makeSelectUserIsCoordinator } from '@pages/PageAuth/selectors/authSelectors';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import { AccessComponentKeyEnum } from '../../role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '../../role/selectors/guardSelector';
import { openEdmInquiry } from '../util/openEdmInquiryModal';
import { setStatusBadgeType, setStatusData, setStatusName } from '../util/setEdmConnectionStatus';
import { sortConnections } from '../util/transformEdmDelivery';
import { EdmConnectionOption } from './EdmConnectionOption';

interface IProps {
    clientId: TClientId;
    edm: IEdmDeliveryInfo;
}

export const EdmConnectionStatus: FC<IProps> = ({ clientId, edm }) => {
    const dispatch = useDispatch();

    const { connectionStatus: tConnectionStatus } = useSelector(makeSelectEdmOperatorConnectionTranslation());
    const isCoordinator = useSelector(makeSelectUserIsCoordinator());
    const isInquiriesEnabled = useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.inquiries));

    const isCustomRoleDocsCheckAvailable = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_DOCS_MANAGE);
    const isCustomRoleInquiriesCheckAvailable = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_INQUIRIES_MANAGE);

    const isEmpoweredUser = isCoordinator || isCustomRoleDocsCheckAvailable;
    const isInquiriesAvailable = isCoordinator || isCustomRoleInquiriesCheckAvailable;

    const sortedConnections = sortConnections(edm.connections);
    const connectionRequest = sortedConnections.map((connection) => {
        const {
            id,
            allowCancel,
            allowResponsibleEdit,
            allowOperatorChange,
            allowPersonNameEdit,
            allowEmailEdit,
            allowPhoneNumberEdit,
            statusId,
            operatorName,
            statusDate,
            creationDate,
            responsibleName,
            responsibleEmail,
            responsiblePhone,
            isRoaming,
        } = connection;

        const statusName = setStatusName(statusId, tConnectionStatus);

        const handleChangeOperator = () => {
            const currentOperatorData = setEdmChangeOperatorInitialValues(connection);

            dispatch(
                modalOpen({
                    modalParams: {
                        isCustomCrossButton: true,
                    },
                    render: ({ onCloseModal }) => (
                        <EdmOperatorConnectionModal
                            clientId={clientId}
                            initialValues={currentOperatorData}
                            macroregionName={edm.macroregionName}
                            mode={EEdmOperatorConnectionMode.changeOperator}
                            onCloseModal={onCloseModal}
                        />
                    ),
                }),
            );
        };

        const handleCancelConnection = () => {
            dispatch(
                modalOpen({
                    modalParams: {
                        isCustomCrossButton: true,
                    },
                    render: ({ onCloseModal }) => (
                        <EdmConnectionCancelModal
                            clientId={clientId}
                            edmConnectionId={EdmConnectionId(id)}
                            operator={operatorName ?? ''}
                            onCloseModal={onCloseModal}
                        />
                    ),
                }),
            );
        };

        const handleEditResponsibleContact = () => {
            dispatch(
                modalOpen({
                    modalParams: {
                        isCustomCrossButton: true,
                    },
                    render: ({ onCloseModal }) => (
                        <EdmResponsibleEditModal
                            clientId={clientId}
                            connection={connection}
                            onCloseModal={onCloseModal}
                        />
                    ),
                }),
            );
        };

        const handleOpenEdmInquiry = () => {
            openEdmInquiry(
                isInquiriesEnabled,
                isInquiriesAvailable,
                edm.inquiryCode,
                dispatch,
                clientId,
                tConnectionStatus.inquiryError,
            );
        };

        const status = setStatusData(statusId, statusName, statusDate, creationDate, tConnectionStatus);

        const isShowDeadlineNotification =
            edm.showDeadlineNotification && statusId === EConnectionStatusId.CONNECTION_IN_PROGRESS;

        const isEditResponsibleDataAvailable = allowPersonNameEdit || allowEmailEdit || allowPhoneNumberEdit;

        const isEditResponsibleAvailable =
            isEmpoweredUser &&
            (statusId === EConnectionStatusId.CONNECTION_IN_PROGRESS || statusId === EConnectionStatusId.CONNECTED) &&
            allowResponsibleEdit &&
            isEditResponsibleDataAvailable;

        const isChangeOperatorAvailable =
            isEmpoweredUser && statusId === EConnectionStatusId.CONNECTED && allowOperatorChange;

        const isAllowCancelAvailable =
            isEmpoweredUser && statusId === EConnectionStatusId.CONNECTION_IN_PROGRESS && allowCancel;

        const isAbsentResponsibleContact = !responsibleName && !responsibleEmail && !responsiblePhone;
        const isShowEmdConnectionOptions =
            isChangeOperatorAvailable || isEditResponsibleAvailable || isAllowCancelAvailable;

        return (
            <EmdConnectionStatusContainerStyled data-name="connection-status-container" key={id}>
                {isShowDeadlineNotification && isEmpoweredUser && isInquiriesAvailable && (
                    <InfoBlock className="connection-deadline_info" colorStatus={EColorStatus.INFO}>
                        <EdmInfoTitleStyled>
                            {tConnectionStatus.deadlineNotification}
                            <LinkBlock className="inquiry-link__button" onClick={handleOpenEdmInquiry}>
                                <EdmInfoLinkStyled>{tConnectionStatus.inquiry}</EdmInfoLinkStyled>
                            </LinkBlock>
                        </EdmInfoTitleStyled>
                    </InfoBlock>
                )}
                <EmdConnectionStatusStyled>
                    <EmdConnectionTitleStyled statusId={statusId}>
                        <EllipseText title={operatorName}>{operatorName}</EllipseText>
                        <BadgeStatus
                            className="edm-connection-status"
                            status={setStatusBadgeType(statusId)}
                            text={statusName}
                        />
                    </EmdConnectionTitleStyled>
                    <EmdConnectionInfoStyled>
                        <EmdConnectionLabelStyled>{tConnectionStatus.status}</EmdConnectionLabelStyled>
                        <EllipseText title={status}>{status}</EllipseText>
                        {isRoaming && (
                            <>
                                <EmdConnectionLabelStyled>{tConnectionStatus.responsible}</EmdConnectionLabelStyled>
                                <EmdResponsibleContactStyled>
                                    {!!responsibleName && <span>{responsibleName}</span>}
                                    {!!responsiblePhone && (
                                        <span>{formatNumberToPhone(responsiblePhone, PhoneFormatType.status)}</span>
                                    )}
                                    {!!responsibleEmail && <span>{responsibleEmail}</span>}
                                    {isAbsentResponsibleContact && <span>{tConnectionStatus.responsibleAbsent}</span>}
                                </EmdResponsibleContactStyled>
                            </>
                        )}
                    </EmdConnectionInfoStyled>
                    {isShowEmdConnectionOptions && (
                        <EmdConnectionOptionsStyled>
                            {isChangeOperatorAvailable && (
                                <EdmConnectionOption
                                    imgHeight="16px"
                                    imgSrc={icons.transactionSmallInfoIcon}
                                    imgWidth="15px"
                                    linkTitle={tConnectionStatus.changeOperatorButton}
                                    onClick={handleChangeOperator}
                                />
                            )}
                            {isEditResponsibleAvailable && (
                                <EdmConnectionOption
                                    imgHeight="16px"
                                    imgSrc={icons.profileIconInfoSmall}
                                    imgWidth="12px"
                                    linkTitle={tConnectionStatus.editResponsibleButton}
                                    onClick={handleEditResponsibleContact}
                                />
                            )}
                            {isAllowCancelAvailable && (
                                <EdmConnectionOption
                                    imgHeight="16px"
                                    imgSrc={icons.deleteIcon}
                                    imgWidth="12px"
                                    linkTitle={tConnectionStatus.cancelButton}
                                    onClick={handleCancelConnection}
                                />
                            )}
                        </EmdConnectionOptionsStyled>
                    )}
                </EmdConnectionStatusStyled>
            </EmdConnectionStatusContainerStyled>
        );
    });

    return (
        <>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.edmOperatorConnection]} />
            {connectionRequest}
        </>
    );
};
